import { useEffect, type ReactNode } from 'react';
import { triggerHaptic } from '../../lib/haptics';

// Anything genuinely tappable. Mirrors the app's `.tap` convention plus the
// native roles so new controls light up haptics for free.
const INTERACTIVE_SELECTOR = 'button, [role="button"], [role="tab"], [role="switch"], a[href], .tap';

/**
 * App-wide native reflex: a light "tap" haptic on every button / link / tappable
 * press, via a single delegated listener — so new screens get it for free. It
 * runs on the bubble phase and de-dupes against any semantic haptic the control
 * fired itself, so a tab/switch/CTA buzzes exactly once (its own, richer haptic),
 * never a doubled light+semantic stutter.
 *
 * (The interactive edge-swipe-to-go-back lives in `NavStack`, which owns the
 * content viewport so the bolted chrome doesn't slide with the page.)
 *
 * On web / non-touch the haptics simply no-op.
 */
export function NativeGestures({ children }: { children: ReactNode }) {
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      // Ignore synthetic / programmatic / keyboard-or-AT-driven clicks — a
      // native tap haptic should only answer a real finger.
      if (!e.isTrusted) return;
      const target = e.target as Element | null;
      const el = target?.closest?.(INTERACTIVE_SELECTOR) as HTMLElement | null;
      if (!el) return;
      if ((el as HTMLButtonElement).disabled) return;
      if (el.getAttribute('aria-disabled') === 'true') return;
      if (el.closest('[data-no-haptic]')) return;
      // Not forced: collapses into the control's own semantic haptic (which, as
      // a React handler, already ran this same click) instead of doubling it.
      void triggerHaptic('light');
    };
    // Bubble phase (not capture) so component-level semantic haptics fire first.
    document.addEventListener('click', onClick, false);
    return () => document.removeEventListener('click', onClick, false);
  }, []);

  return <div className="h-full">{children}</div>;
}
